<script setup>
  import {inject, ref, watch} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentRecordShareCompleteDialog",
  });

  const props = defineProps({
    record: {
      type: Object,
      default: () => {
        return null;
      },
    },
    sharedrecordId: {
      type: String,
      default: () => {
        return null;
      },
    },
  });

  const emit = defineEmits(["completed"]);

  const privacy = inject("$privacy");

  const refDialog = ref(null);
  const currentRecord = ref(props.record);
  const currentSharedrecordId = ref(props.sharedrecordId);

  const form = useForm({
    info: null,
  });

  watch(
    () => props.record,
    (record) => {
      currentRecord.value = record;
    },
  );

  function open(sharedrecordId) {
    if (sharedrecordId !== undefined) {
      form.reset();
      form.wasSuccessful = false;
      currentSharedrecordId.value = sharedrecordId;
    }
    refDialog.value.open();
  }

  function close() {
    refDialog.value.close();
  }

  const complete = async () => {
    const record = await privacy.decryptRecord(currentRecord.value);

    form
      .transform((data) => ({
        ...data,
        info: record.info,
      }))
      .patch(
        route("sharedrecords.update", {
          patient: currentRecord.value.patient_id,
          record: currentRecord.value.id,
          sharedrecord: currentSharedrecordId.value,
        }),
        {
          only: ["flash", "errors"],
          preserveScroll: true,
          onSuccess: () => {
            emit("completed");
          },
        },
      );
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-width="small"
    :dialog-title="form.wasSuccessful ? 'Zurücksenden erfolgreich' : 'Sind Sie sicher?'"
    :test-id="getTestId('complete')"
    :is-loading="form.processing"
  >
    <template #content>
      <div v-if="form.wasSuccessful">Diese Variante wurde an den Absender zurückgesendet.</div>

      <div v-else>
        Sie senden diese Variante mit den ggf. von Ihnen vorgenommenen Änderungen an den Absender zurück.
      </div>
    </template>

    <template #actions>
      <template v-if="!form.wasSuccessful">
        <MedButton
          variant="primary"
          :disabled="form.processing"
          :data-test="getTestId('complete-dialog-send-back-button')"
          @click="complete()"
        >
          Zurücksenden
        </MedButton>
      </template>

      <MedButton :data-test="getTestId('complete-dialog-close-button')" @click="close()">
        <template v-if="form.wasSuccessful">Schließen</template>
        <template v-else>Abbrechen</template>
      </MedButton>
    </template>
  </ComponentDialog>
</template>
